export type Locale = 'en' | 'de'

export const LOCALES: Locale[] = ['en', 'de']

export const LOCALE_LABELS: Record<Locale, string> = {
  en: 'English',
  de: 'Deutsch',
}

export const LOCALE_HTML_LANG: Record<Locale, string> = {
  en: 'en',
  de: 'de',
}

export const LOCALE_OG: Record<Locale, string> = {
  en: 'en_US',
  de: 'de_DE',
}

type Dict = {
  // Header
  'header.brand': string
  'header.nav.vision': string
  'header.nav.technical': string
  'header.nav.team': string
  'header.nav.revenue': string
  'header.nav.roadmap': string
  'header.searchPlaceholder': string
  'header.searchAria': string
  'header.openMenu': string
  'header.closeMenu': string
  'header.getStarted': string
  'header.language': string

  // Search modal
  'search.placeholder': string
  'search.noResults': string
  'search.selectHint': string
  'search.navigateHint': string
  'search.closeHint': string

  // Sidebar / nav
  'sidebar.aria': string
  'sidebar.sectionsAria': string
  'sidebar.mobileAria': string

  // FAQ section titles (used in nav, footer, breadcrumbs)
  'section.vision-product-market': string
  'section.technical': string
  'section.team-trust': string
  'section.revenue': string
  'section.participation': string

  // Footer
  'footer.topics': string
  'footer.moreTopics': string
  'footer.copyright': string
  'footer.twitterAria': string

  // DocPage
  'doc.previous': string
  'doc.next': string
  'doc.home': string
  'doc.keyTakeaways': string
  'doc.faqLabel': string
  'doc.mainAria': string
  'doc.tocOpen': string

  // TableOfContents
  'toc.title': string
  'toc.sectionsSingular': string
  'toc.sectionsPlural': string
  'toc.backToTop': string
  'toc.scrollProgress': string

  // HomePage
  'home.kicker': string
  'home.title': string
  'home.subtitle': string
  'home.browseAll': string
  'home.card.vision.title': string
  'home.card.vision.desc': string
  'home.card.technical.title': string
  'home.card.technical.desc': string
  'home.card.team.title': string
  'home.card.team.desc': string
  'home.card.revenue.title': string
  'home.card.revenue.desc': string
  'home.card.roadmap.title': string
  'home.card.roadmap.desc': string
  'home.popular': string
  'home.popular.whatIs': string
  'home.popular.dynamicFee': string
  'home.popular.marketplace': string
  'home.popular.projections': string

  // SEO
  'seo.home.title': string
  'seo.home.titleSuffix': string
  'seo.home.description': string
  'seo.faq.titleSuffix': string
}

export const translations: Record<Locale, Dict> = {
  en: {
    'header.brand': 'rex FAQ',
    'header.nav.vision': 'Vision',
    'header.nav.technical': 'Technical',
    'header.nav.team': 'Team',
    'header.nav.revenue': 'Revenue',
    'header.nav.roadmap': 'Roadmap',
    'header.searchPlaceholder': 'Search FAQ...',
    'header.searchAria': 'Search',
    'header.openMenu': 'Open menu',
    'header.closeMenu': 'Close menu',
    'header.getStarted': 'Get Started',
    'header.language': 'Language',

    'search.placeholder': 'Search FAQ...',
    'search.noResults': 'No results found for',
    'search.selectHint': 'to select',
    'search.navigateHint': 'to navigate',
    'search.closeHint': 'to close',

    'sidebar.aria': 'FAQ Navigation',
    'sidebar.sectionsAria': 'FAQ sections',
    'sidebar.mobileAria': 'Navigation menu',

    'section.vision-product-market': 'Vision, Product & Market',
    'section.technical': 'Technical Architecture',
    'section.team-trust': 'Team & Trust',
    'section.revenue': 'Revenue & Economics',
    'section.participation': 'Participation & Roadmap',

    'footer.topics': 'FAQ Topics',
    'footer.moreTopics': 'More Topics',
    'footer.copyright': '\u00A92026 RexHook',
    'footer.twitterAria': 'Twitter',

    'doc.previous': 'Previous',
    'doc.next': 'Next',
    'doc.home': 'Home',
    'doc.keyTakeaways': 'Key Takeaways',
    'doc.faqLabel': 'FAQ Content',
    'doc.mainAria': 'FAQ Content',
    'doc.tocOpen': 'Open table of contents',

    'toc.title': 'On this page',
    'toc.sectionsSingular': 'section',
    'toc.sectionsPlural': 'sections',
    'toc.backToTop': 'Back to top',
    'toc.scrollProgress': 'Scroll progress',

    'home.kicker': 'FAQ',
    'home.title': 'Rex Hook FAQ',
    'home.subtitle': 'Questions answered about vision, technical architecture, team, revenue, and how to participate. Everything investors, developers, creators & partners need to know.',
    'home.browseAll': 'Browse all questions',
    'home.card.vision.title': 'Vision, Product & Market',
    'home.card.vision.desc': 'What RexHook is, why it exists, and how it positions in the market. Learn the fundamentals behind the protocol.',
    'home.card.technical.title': 'Technical Architecture',
    'home.card.technical.desc': 'How the technology works under the hood. Hooks, fees, MEV, and gas.',
    'home.card.team.title': 'Team & Trust',
    'home.card.team.desc': "Who builds this, why it's credible, and how trust is established.",
    'home.card.revenue.title': 'Revenue & Economics',
    'home.card.revenue.desc': 'How money flows through the protocol and how holders benefit.',
    'home.card.roadmap.title': 'Participation & Roadmap',
    'home.card.roadmap.desc': 'How to get involved, go-to-market plan, deployment timeline, and what comes next.',
    'home.popular': 'Popular Questions',
    'home.popular.whatIs': 'What is RexHook?',
    'home.popular.dynamicFee': 'DynamicFeeHook',
    'home.popular.marketplace': 'Hook Marketplace',
    'home.popular.projections': 'Revenue Projections',

    'seo.home.title': 'RexHook FAQ',
    'seo.home.titleSuffix': 'The Infrastructure Layer for Uniswap V4 Hooks',
    'seo.home.description': 'The open infrastructure layer for Uniswap V4 hooks. Questions answered about vision, technical architecture, team, revenue, and participation.',
    'seo.faq.titleSuffix': 'RexHook FAQ',
  },

  de: {
    'header.brand': 'rex FAQ',
    'header.nav.vision': 'Vision',
    'header.nav.technical': 'Technik',
    'header.nav.team': 'Team',
    'header.nav.revenue': 'Umsatz',
    'header.nav.roadmap': 'Roadmap',
    'header.searchPlaceholder': 'FAQ durchsuchen...',
    'header.searchAria': 'Suchen',
    'header.openMenu': 'Men\u00FC \u00F6ffnen',
    'header.closeMenu': 'Men\u00FC schlie\u00DFen',
    'header.getStarted': 'Loslegen',
    'header.language': 'Sprache',

    'search.placeholder': 'FAQ durchsuchen...',
    'search.noResults': 'Keine Ergebnisse f\u00FCr',
    'search.selectHint': 'zum Ausw\u00E4hlen',
    'search.navigateHint': 'zum Navigieren',
    'search.closeHint': 'zum Schlie\u00DFen',

    'sidebar.aria': 'FAQ-Navigation',
    'sidebar.sectionsAria': 'FAQ-Bereiche',
    'sidebar.mobileAria': 'Navigationsmen\u00FC',

    'section.vision-product-market': 'Vision, Produkt & Markt',
    'section.technical': 'Technische Architektur',
    'section.team-trust': 'Team & Vertrauen',
    'section.revenue': 'Umsatz & \u00D6konomie',
    'section.participation': 'Teilnahme & Roadmap',

    'footer.topics': 'FAQ-Themen',
    'footer.moreTopics': 'Weitere Themen',
    'footer.copyright': '\u00A92026 RexHook',
    'footer.twitterAria': 'Twitter',

    'doc.previous': 'Zur\u00FCck',
    'doc.next': 'Weiter',
    'doc.home': 'Start',
    'doc.keyTakeaways': 'Kernaussagen',
    'doc.faqLabel': 'FAQ-Inhalt',
    'doc.mainAria': 'FAQ-Inhalt',
    'doc.tocOpen': 'Inhaltsverzeichnis \u00F6ffnen',

    'toc.title': 'Auf dieser Seite',
    'toc.sectionsSingular': 'Abschnitt',
    'toc.sectionsPlural': 'Abschnitte',
    'toc.backToTop': 'Nach oben',
    'toc.scrollProgress': 'Scroll-Fortschritt',

    'home.kicker': 'FAQ',
    'home.title': 'Rex Hook FAQ',
    'home.subtitle': 'Antworten zu Vision, technischer Architektur, Team, Umsatz und Teilnahme. Alles, was Investoren, Entwickler, Creator & Partner wissen m\u00FCssen.',
    'home.browseAll': 'Alle Fragen ansehen',
    'home.card.vision.title': 'Vision, Produkt & Markt',
    'home.card.vision.desc': 'Was RexHook ist, warum es existiert und wie es sich am Markt positioniert. Lerne die Grundlagen hinter dem Protokoll kennen.',
    'home.card.technical.title': 'Technische Architektur',
    'home.card.technical.desc': 'Wie die Technologie unter der Haube funktioniert. Hooks, Geb\u00FChren, MEV und Gas.',
    'home.card.team.title': 'Team & Vertrauen',
    'home.card.team.desc': 'Wer das aufbaut, warum es glaubw\u00FCrdig ist und wie Vertrauen geschaffen wird.',
    'home.card.revenue.title': 'Umsatz & \u00D6konomie',
    'home.card.revenue.desc': 'Wie Geld durch das Protokoll flie\u00DFt und wie Inhaber davon profitieren.',
    'home.card.roadmap.title': 'Teilnahme & Roadmap',
    'home.card.roadmap.desc': 'Wie du dich beteiligen kannst, Go-to-Market-Plan, Deployment-Zeitplan und was als N\u00E4chstes kommt.',
    'home.popular': 'H\u00E4ufige Fragen',
    'home.popular.whatIs': 'Was ist RexHook?',
    'home.popular.dynamicFee': 'DynamicFeeHook',
    'home.popular.marketplace': 'Hook-Marktplatz',
    'home.popular.projections': 'Umsatzprognosen',

    'seo.home.title': 'RexHook FAQ',
    'seo.home.titleSuffix': 'Die Infrastruktur-Schicht f\u00FCr Uniswap V4 Hooks',
    'seo.home.description': 'Die offene Infrastruktur-Schicht f\u00FCr Uniswap V4 Hooks. Antworten zu Vision, technischer Architektur, Team, Umsatz und Teilnahme.',
    'seo.faq.titleSuffix': 'RexHook FAQ',
  },
}

export type TranslationKey = keyof Dict
